import { useTranslation } from 'react-i18next';
import { RebootDeviceButton } from '../_components/wired/RebootDeviceButton';
import { MiniHubSupportPanel } from './MiniHubSupportPanel';
import { DEVICE_TYPES, PROXIMITY_SIZES, ACCESS_TYPES, isEasyLock } from "../_constants";
import { ReadOnlyDataRow } from "../_components/ReadOnlyDataRow";
import { DeviceHelper, TimeHelper } from "../_helpers";
import { Device } from "../_models/Device";
import { DeviceFunctionality } from "../_helpers/DeviceFunctionality";
import ToolTipWrapper from "../_components/ToolTipWrapper";
import { SimpleToggle } from "../_components/SimpleToggle";
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { devicesActions } from '../_actions';
import { useError } from '../_hooks/useError';

export function DeviceAdvancedConfig({
    devices = [Device()],
    device = Device(),
    canViewAdvancedConfig = false,
    className = '',
    // Sólo para que los tests del panel de soporte no tengan que esperar
    // medio minuto a que un equipo simulado conteste.
    statusPollMs = undefined as number | undefined,
    statusWaitMs = undefined as number | undefined,
    updateWaitMs = undefined as number | undefined
}) {

    //LIBRARIES
    const { t } = useTranslation();
    const type = device.type;
    const dispatch = useDispatch();

    //LOCAL STATE
    const hasAdvancedSettings =         DeviceFunctionality.hasAdvancedSettings(type)
    const supportsRemoteAccess =        DeviceFunctionality.supportsRemoteAccess(type);
    const supportsTimer =               DeviceFunctionality.supportsTimer(type);
    const supportsProximity =           DeviceFunctionality.supportsProximity(type);
    const supportsArrivalExitOptions =  DeviceFunctionality.supportsArrivalExitOptions(type);
    const supportsAccessLevels =        DeviceFunctionality.supportsAccessLevels(type);
    const supportsAccessType =          DeviceFunctionality.supportsAccessType(type);
  
    const supportsWlanNetwork =         DeviceFunctionality.supportsWlanNetwork(type);
    // Keypads with a SIM report which link carries their traffic; the signal
    // row is that link's signal (the modem's when "sim"). It has to be up here
    // with the other consts: the row helpers below run while the JSX returns,
    // which is before anything declared after the return -- a const read there
    // is in the temporal dead zone and throws, taking the whole panel down.
    const isOnSim = device.internet_routing === "sim";
    // Two different scales share the row. WiFi: usable from about -60, bad
    // below -81. Cellular (AT+CSQ maps 0..31 onto -113..-51 dBm): -75 is a
    // good signal, not a middling one, and anything under -100 is unusable.
    const poorBelow = isOnSim ? -100 : -81;
    const goodFrom = isOnSim ? -85 : -60;
    const supportsEthernetNetwork =     DeviceFunctionality.supportsEthernetNetwork(type);
    const supportsRemoteOTA =           DeviceFunctionality.supportsRemoteOTA({type});
    const isMiniHub =                   DeviceFunctionality.supportsMiniHubSupportOrders({type});
    const hasNFC =                      DeviceFunctionality.supportsNFC(type);
    const hasKeypad =                   type === DEVICE_TYPES.Keypad;
    const hasSim =                      DeviceFunctionality.supportsSim({type});

    const { errorMessage, setError } = useError();

    const [isRemote, setIsRemote] = useState(device?.remote);

    useEffect(() => setIsRemote(device?.remote), [device])    

    const toggleRemoteAccess = useCallback(async () => {
        const remote = isRemote;
        setIsRemote(!remote);
        try {
            await dispatch(devicesActions.updateDeviceRemoteAccess(device.uid, !remote))
        } catch {
            setError(t('mod_devices_unable_to_change_remote_access'));
            setIsRemote(remote);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device.uid, dispatch, isRemote]);

    //VIEW
    if (!canViewAdvancedConfig || !hasAdvancedSettings)
        return null;

    return (
        <div className={`${className} d-flex justify-content-between flex-column flex-grow-1`}>
            <div>
                {title()}
                <hr className="m-0"/>
                {advancedSettings()} 
                {isMiniHub && <MiniHubSupportPanel
                    /* key on the uid: this page is NOT remounted when another
                     * device is opened (the secondary view is already the
                     * config one), so without it the panel kept the previous
                     * device's status, chosen version and log level -- and its
                     * buttons acted on the new uid with the old choices. */
                    key={device.uid}
                    uid={device.uid}
                    version={device.version}
                    versionBle={device.versionBle}
                    hasNfc={hasNFC}
                    hasKeypad={hasKeypad}
                    hasSim={hasSim}
                    {...(statusPollMs === undefined ? {} : { statusPollMs })}
                    {...(statusWaitMs === undefined ? {} : { statusWaitMs })}
                    {...(updateWaitMs === undefined ? {} : { updateWaitMs })}/>}
            </div>
            {supportsRemoteOTA && rebootButtonSection()}
        </div>
    );

    function advancedSettings() {
        return (
            <div className='mt-2'>
                {supportsRemoteAccess && remoteAccess()}
                {supportsTimer && timerInfo()}
                {supportsProximity && proximity()}
                {supportsArrivalExitOptions && garageDoorArrivalExitOptions()}
                {supportsAccessLevels && accessLevel()}
                {supportsAccessType && accessType()}
                {supportsEthernetNetwork && eth_status()}
                {supportsWlanNetwork && wlan_status()}
                {supportsWlanNetwork && internet_routing()}
                {supportsWlanNetwork && wlan_ssid()}
                {supportsWlanNetwork && wifi_rssi()}
                {hasNFC && syncronizedNfcCards()}
                {isEasyLock(device) && easyLockAdvancedConfig()}
                {<div className="text-right mt-1 mr-1 text-danger">{errorMessage}</div>}
            </div>
        );
    }

    function easyLockAdvancedConfig() {
        return (
            <>
                <ReadOnlyDataRow
                    data={<ToolTipWrapper tooltip={t('mod_device_calibration_power_explanation')}><div>{device.lockPower ?? t('common.notConfigured')}</div></ToolTipWrapper>}
                    name={t("mod_device_calibration_power")}
                />
                <ReadOnlyDataRow
                    data={device.isIrEnabled ? t("global_yes") : t("global_no")}
                    name={t("mod_device_ir_sensor")}
                />
                <ReadOnlyDataRow
                    data={device.knockTimeout ? device.knockTimeout + 's' : t('common.notConfigured')}
                    name={t("mod_device_knock_time")}
                />
            </>
        );
    }

    function rebootButtonSection() {
        return (
            <div className="pb-2 d-flex justify-content-end">
                {<RebootDeviceButton name={device.description} uid={device.uid} isEnabled={device.wlan_status === t("status.connected")} requiresConfirmation/>}
            </div>
        );
    }

    function title() {
        return (
            <div className="
                text-uppercase
                font-weight-bold
            ">
                {t("webapp_global_advanced")}
            </div>
        );
    }

    function remoteAccess() {
        if (device.remote === undefined)
            return "MISSING REMOTE DATA";

        return (
            <ReadOnlyDataRow
                className='pr-0'
                hasSeparators={false}
                name={t("webapp_device_remote_access")}
                data={<SimpleToggle handleChange={toggleRemoteAccess} checked={isRemote} size='SMALL' />}
            />
        );
    }

    function garageDoorArrivalExitOptions() {
        return (
            <>
            {arrivalElements()}
            {exitElements()}
            </>
        );

        function arrivalElements() {
            return (
                <>
                {onArrivalVehicle()}
                {onArrivalWalking()}
                </>
            );
        }

        function exitElements() {
            if (DeviceHelper.hasDeviceChildOfType(
                device,
                devices,
                DEVICE_TYPES.MovementSafeAccess,
            )) {
                return (
                    <>
                    {onExitVehicle()}
                    {onExitWalking()}
                    </>
                );
            }

            return null;
        }
    }

    function onArrivalWalking() {
        if (device.accessEntryWalk === undefined)
            return "MISSING WALK DATA";

        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_on_arrival_waking")}
            data={getOnArrivaExitlUiString(device.accessEntryWalk)}
        />;
    }

    function onArrivalVehicle() {
        if (device.accessEntryVehicle === undefined)
            return "MISSING VEHICLE DATA";

        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_on_arrival_vehicle")}
            data={getOnArrivaExitlUiString(device.accessEntryVehicle)}
        />;
    }

    function onExitWalking() {
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_on_exit_waking")}
            data={getOnArrivaExitlUiString(device.accessExitWalk)}
        />;
    }

    function onExitVehicle() {
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_on_exit_vehicle")}
            data={getOnArrivaExitlUiString(device.accessExitVehicle)}
        />;
    }

    function proximity() {
        if (device.extender === undefined
            || device.beacon === undefined
        )
            return "MISSING PROXIMITY DATA";

        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_proximity")}
            data={getString()}
        />;

        function getString() {
            if (!device.beacon)
                return t("webapp_global_disabled");

            return getUiStringForProximitySize(
                DeviceHelper.getProximitySize(device)
            )
        }
    }

    function accessLevel() {
        if (device.accessLevel === undefined)
            return "MISSING ACCESS LEVEL DATA";
        
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_access_level")}
            data={device.accessLevel + 1}
        />;
    }

    function accessType() {
        if (device.accessType === undefined)
            return "MISSING ACCESS TYPE DATA";

        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_access_type")}
            data={getUiStringForAccessType(device.accessType)}
        />;
    }

    function timerInfo() {
        if (device.timer === undefined)
            return "MISSING TIMER DATA";

        let text = t("webapp_device_turn_off_automatically");
        switch(device.type) {
            case DEVICE_TYPES.EasyAccess:
            case DEVICE_TYPES.EasyAccessContact:
            case DEVICE_TYPES.EasyAccessContactNFC:
            case DEVICE_TYPES.EasyAccessContactNFCWifi:
            case DEVICE_TYPES.EasyIntegrations:
            case DEVICE_TYPES.Keypad:
                text = t("global_device_setting_opening_duration");
                break;
            default:
                break;
        }

        return <ReadOnlyDataRow
            hasSeparators={false}
            name={text}
            data={device.timer ? TimeHelper.secondsToTimeString(device.timer) : t('common.notConfigured')}
        />;
    }

    

    function wlan_ssid() {
        // On SIM the firmware reports no SSID, and a label with nothing under
        // it reads as a bug rather than as "there is no WiFi in use".
        if (!device.wlan_ssid) return null;
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_wlan_ssid")}
            data={device.wlan_ssid}
        />;
    }

    function internet_routing() {
        const routing = device.internet_routing;
        if (!routing || routing === "none") return null;
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_internet_routing")}
            data={t(`webapp_device_internet_routing_${routing}`)}
        />;
    }

    function wifi_rssi() {
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t(isOnSim ? "webapp_device_sim_rssi" : "webapp_device_wifi_rssi")}
            data={device.wifi_rssi == undefined ? '--' :
                device.wifi_rssi < poorBelow ?
                t('webapp_device_wifi_rssi_status_poor', {rssi:device.wifi_rssi}) :
                device.wifi_rssi < goodFrom ?
                t('webapp_device_wifi_rssi_status_normal', {rssi:device.wifi_rssi}) :
                t('webapp_device_wifi_rssi_status_good', {rssi:device.wifi_rssi})
            }
        />;
    }

    function wlan_status() {
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_wlan_status")}
            data={device.wlan_status}
        />;
    }

    function eth_status() {
        return <ReadOnlyDataRow
            hasSeparators={false}
            name={t("webapp_device_eth_status")}
            data={device.eth_status}
        />;
    }

    function syncronizedNfcCards() {
        return (
            <ReadOnlyDataRow
                hasSeparators={false}
                name={t("mod_cards_nfc_cards")}
                data={
                    device?.isNfcCardsSynchronized
                        ? t("mod_cards_nfc_cards_all_synchronized")
                        : t("mod_cards_nfc_cards_to_synchronize")
                }
            />
        );
    }

    //STRING MAPPINGS
    function getOnArrivaExitlUiString(value) {
        const mapping = {
            0: t("webapp_global_nothing"),
            1: t("webapp_global_open"),
            2: t("webapp_global_ask"),
        };

        return mapping[value] || "UNDEFINED VALUE";
    }

    function getUiStringForProximitySize(proximitySize) {
        const mapping = {
            [PROXIMITY_SIZES.SMALL]: t("webapp_global_small"),
            [PROXIMITY_SIZES.MEDIUM]: t("webapp_global_medium"),
            [PROXIMITY_SIZES.LARGE]: t("webapp_global_large"),
            [PROXIMITY_SIZES.CUSTOM]: t("global_custom"),
        };
        
        return mapping[proximitySize] || "UNDEFINED PROXIMITY SIZE";
    }

    function getUiStringForAccessType(accessType) {
        const mapping = {
            [ACCESS_TYPES.GARAGE]: t("webapp_global_garage_door"),
            [ACCESS_TYPES.DOOR]: t("webapp_global_entry_door"),
            [ACCESS_TYPES.CONTACT]: t("webapp_global_contact"),
            [ACCESS_TYPES.ENTRY]: t("webapp_global_entry"),
            [ACCESS_TYPES.EXIT]: t("webapp_global_exit_door"),
        };

        return mapping[accessType] || "UNDEFINED ACCESS TYPE";
    }
}
